import { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Droppable } from 'react-beautiful-dnd';
import PropTypes from 'prop-types';
import NegociosCard from './NegociosCard';
import DrawerChatWhatsapp from 'src/sections/@chats/chat/DrawerChatWhatsapp';
import { setLeadData } from 'src/redux/slices/crm';
import Iconify from 'src/components/Iconify';
import { Input } from 'src/@/components/ui/input';
import { Button } from 'src/@/components/ui/button';
import { Card } from 'src/@/components/ui/card';

// ----------------------------------------------------------------------

KanbanColumn.propTypes = {
  column: PropTypes.object,
  index: PropTypes.number,
};

export default function KanbanColumn({ user, loadingKanban, cliente, column, callbackRefreshLeads, onSelect }) {
  const dispatch = useDispatch();
  const { leadsKanban } = useSelector((state) => state.crm);
  const { nome, leadIds, id } = column;
  const [openDrawerChatWhatsapp, setOpenDrawerChatWhatsapp] = useState(false);

  const [displayedCount, setDisplayedCount] = useState(5);
  const [isDescending, setIsDescending] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [showSearch, setShowSearch] = useState(false);

  const toggleSortOrder = () => {
    setIsDescending((prev) => !prev);
  };

  const filteredLeadIds = leadIds.filter((leadId) => {
    const lead = leadsKanban[leadId];
    if (!lead) return false;
    const searchLower = searchTerm.toLowerCase();
    const nomeMatch = lead?.nome?.toLowerCase().includes(searchLower) || lead?.nome === null;
    const telefoneMatch = lead?.contato?.telefone?.includes(searchTerm) || false;
    const whatsappMatch = lead?.whatsapp?.numero?.includes(searchTerm) || false;
    return nomeMatch || telefoneMatch || whatsappMatch;
  });

  const sortedLeadIds = [...filteredLeadIds].sort((a, b) => {
    const dateA = new Date(leadsKanban[a]?.data_modificacao || 0);
    const dateB = new Date(leadsKanban[b]?.data_modificacao || 0);
    return isDescending ? dateB - dateA : dateA - dateB;
  });

  const totalValue = sortedLeadIds.reduce((sum, leadId) => {
    const lead = leadsKanban[leadId];
    return sum + (lead?.valor || 0);
  }, 0);

  const handleLoadMore = () => {
    setDisplayedCount((prev) => Math.min(prev + 10, sortedLeadIds.length));
  };

  useEffect(() => { }, [leadsKanban, leadIds, isDescending, searchTerm]);

  async function handlOpenLead(data) {
    dispatch(setLeadData(data));
  }

  // Generate a deterministic color from the column name
  const columnColor = (() => {
    const colors = ['#7C3AED', '#3b82f6', '#10b981', '#f59e0b', '#ef4444', '#ec4899', '#06b6d4', '#8b5cf6', '#14b8a6', '#f97316'];
    if (!nome) return colors[0];
    let hash = 0;
    for (let i = 0; i < nome.length; i++) {
      hash = nome.charCodeAt(i) + ((hash << 5) - hash);
    }
    return colors[Math.abs(hash) % colors.length];
  })();

  return (
    <>
      <DrawerChatWhatsapp
        open={openDrawerChatWhatsapp}
        onClose={() => setOpenDrawerChatWhatsapp(false)}
        cliente={cliente}
        user={user}
      />

      <Card className="rounded-2xl bg-gray-100/80 dark:from-[#1A1D21] dark:to-[#1A1D21]/80 overflow-hidden flex flex-col transition-all min-w-[230px] flex-1 h-full">
        {/* Column Header */}
        <div className="px-2.5 py-2 flex flex-col gap-1.5 shrink-0">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2.5">
              <span className="text-[13px] font-bold text-gray-700 dark:text-[#D1D2D3] tracking-tight">{nome}</span>
            </div>
            <div className="flex items-center gap-1.5">
              <button
                onClick={() => setShowSearch(v => !v)}
                className="w-6 h-6 rounded-lg flex items-center justify-center text-gray-400 dark:text-[#ABABAD] hover:bg-gray-200/50 dark:hover:bg-[#383838] hover:text-gray-600 dark:hover:text-[#D1D2D3] transition-colors"
                title="Pesquisar"
              >
                <Iconify icon="eva:search-outline" width={14} />
              </button>
              <button
                onClick={toggleSortOrder}
                className="w-6 h-6 rounded-lg flex items-center justify-center text-gray-400 dark:text-[#ABABAD] hover:bg-gray-200/50 dark:hover:bg-[#383838] hover:text-gray-600 dark:hover:text-[#D1D2D3] transition-colors"
                title={isDescending ? 'Mais recentes primeiro' : 'Mais antigos primeiro'}
              >
                <Iconify icon={isDescending ? 'octicon:sort-asc-16' : 'octicon:sort-desc-16'} width={14} />
              </button>
            </div>
          </div>

          {/* Contagem e valor total */}
          <div className="flex items-center gap-2 text-[11px] text-gray-500 dark:text-[#ABABAD]">
            <span className="font-medium">{leadIds.length} negócio{leadIds.length !== 1 ? 's' : ''}</span>
            {totalValue > 0 && (
              <>
                <span className="text-gray-300 dark:text-[#383838]">·</span>
                <span className="font-semibold">R$ {totalValue.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}</span>
              </>
            )}
          </div>

          {/* Search input */}
          {showSearch && (
            <div className="relative">
              <Input
                placeholder="Pesquisar..."
                className="h-8 text-xs bg-slate-50 dark:bg-[#222529] border-gray-200 dark:border-[#383838] text-gray-900 dark:text-[#D1D2D3] focus:ring-[#7C3AED]/20 dark:placeholder-[#ABABAD] pl-8 rounded-lg"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                autoFocus
              />
              <Iconify icon="eva:search-outline" width={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-400 dark:text-[#ABABAD]" />
              {searchTerm && (
                <button
                  onClick={() => { setSearchTerm(''); setShowSearch(false); }}
                  className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 dark:hover:text-[#D1D2D3]"
                >
                  <Iconify icon="eva:close-outline" width={14} />
                </button>
              )}
            </div>
          )}
        </div>

        {/* Cards Area */}
        <Droppable droppableId={id} type="task">
          {(provided, snapshot) => (
            <div
              className={`px-2 pb-2 flex-1 min-h-0 overflow-y-auto space-y-1.5 transition-colors ${snapshot.isDraggingOver ? 'bg-[#7C3AED]/[0.03]' : ''}`}
              ref={provided.innerRef}
              {...provided.droppableProps}
            >
              {loadingKanban ? (
                // Skeleton loading
                <div className="space-y-2.5">
                  {[1, 2, 3].map((i) => (
                    <div key={i} className="bg-slate-50 dark:bg-[#222529] rounded-xl border border-gray-100 dark:border-[#383838] p-3.5 animate-pulse">
                      <div className="flex items-center justify-between mb-3">
                        <div className="h-3 w-16 bg-gray-200 dark:bg-[#383838] rounded" />
                        <div className="h-3 w-12 bg-gray-200 dark:bg-[#383838] rounded" />
                      </div>
                      <div className="h-4 w-3/4 bg-gray-200 dark:bg-[#383838] rounded mb-2" />
                      <div className="h-3 w-1/2 bg-gray-200 dark:bg-[#383838] rounded mb-3" />
                      <div className="flex items-center justify-between pt-2 border-t border-gray-50 dark:border-[#383838]/50">
                        <div className="flex items-center gap-2">
                          <div className="w-6 h-6 rounded-full bg-gray-200 dark:bg-[#383838]" />
                          <div className="h-3 w-20 bg-gray-200 dark:bg-[#383838] rounded" />
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : sortedLeadIds.length === 0 ? (
                <div className="bg-slate-50 dark:bg-[#222529] rounded-xl border border-gray-100 dark:border-[#383838] p-3.5 border-dashed">
                  <div className="flex items-center justify-between mb-3">
                    <div className="h-3 w-16 bg-gray-200 dark:bg-[#383838] rounded opacity-50" />
                    <div className="h-3 w-12 bg-gray-200 dark:bg-[#383838] rounded opacity-50" />
                  </div>
                  <div className="h-4 w-3/4 bg-gray-200 dark:bg-[#383838] rounded mb-2 opacity-50" />
                  <div className="h-3 w-1/2 bg-gray-200 dark:bg-[#383838] rounded mb-3 opacity-50" />
                  <div className="flex items-center justify-between pt-2 border-t border-gray-50 dark:border-[#383838]/50">
                    <div className="flex items-center gap-2">
                      <div className="w-6 h-6 rounded-full bg-gray-200 dark:bg-[#383838] opacity-50" />
                      <div className="h-3 w-20 bg-gray-200 dark:bg-[#383838] rounded opacity-50" />
                    </div>
                  </div>
                  <div className="text-center mt-4 text-gray-300 dark:text-[#ABABAD]/30">
                    <span className="text-[11px] font-medium">
                      {searchTerm ? 'Nenhum resultado' : 'Arraste leads aqui'}
                    </span>
                  </div>
                </div>
              ) : (
                leadsKanban &&
                sortedLeadIds
                  .slice(0, displayedCount)
                  .map((cardId, index) => (
                    <NegociosCard
                      callbackRefreshLeads={callbackRefreshLeads}
                      onOpen={() => onSelect(cardId)}
                      userId={user.id}
                      rules={user.rules}
                      key={cardId}
                      card={leadsKanban[cardId]}
                      index={index}
                    />
                  ))
              )}
              {provided.placeholder}

              {/* Load more button */}
              {displayedCount < sortedLeadIds.length && (
                <div className="flex justify-center pt-2">
                  <button
                    onClick={handleLoadMore}
                    className="text-[11px] font-medium text-gray-500 dark:text-[#ABABAD] hover:text-[#7C3AED] dark:hover:text-[#7C3AED] px-4 py-1.5 rounded-full bg-slate-50 dark:bg-[#222529] border border-gray-200 dark:border-[#383838] hover:border-[#7C3AED]/30 dark:hover:border-[#7C3AED]/30 transition-all"
                  >
                    Carregar mais ({sortedLeadIds.length - displayedCount} restantes)
                  </button>
                </div>
              )}
            </div>
          )}
        </Droppable>
      </Card>
    </>
  );
}
